<template>
  <div class="p-config-keywords-add">
    <div class="p-config-keywords-add__content">
      <p class="p-config-keywords-add__title">
        1. 順位取得を行うキーワードを入力しましょう
      </p>
      <div class="c-fieldset">
        <div class="c-fieldset__content c-alertPos">
          <Input
            v-model="params.keyword"
            name="keyword"
            placeholder="（例）seo対策 キーワード"
            :class="{
              alert: isEdited.keyword && (error.keyword || error.id)
            }"
            v-focus="isFocus"
            @focusout="focusoutKeyword"
          />
          <Alert v-if="isEdited.keyword && error.keyword">
            {{ error.keyword }}
          </Alert>
          <Alert v-else-if="isEdited.keyword && isEdited.url && error.id">
            {{ error.id }}
          </Alert>
        </div>
        <div class="c-fieldset__btn">
          <Button
            variant="main"
            fsize="f14"
            class="l-w-240"
            :class="{ 'is-loading': candidate.isLoading }"
            :disabled="
              isLoading || error.keyword
                ? true
                : false || candidate.isVisible || candidate.isLoading
            "
            @click="getCandidates"
          >
            順位データを検索する
          </Button>
        </div>
      </div>
    </div>
    <div class="p-config-keywords-add__content">
      <p class="p-config-keywords-add__title">
        2. Search Console
        より候補となるURL・キーワードを選択する、もしくは、URLを指定して登録する
      </p>
      <div
        v-if="!candidate.isLoading && candidate.isVisible"
        class="p-config-keywords-add__item"
      >
        <p class="p-config-keywords-add__title--sub">
          「<strong>{{ params.keyword }}</strong
          >」を含むキーワードで直近30日間の順位データがあるURL候補
        </p>
        <KeywordCandidates
          v-model="params.checked"
          input="checkbox"
          :items="candidate.items"
          :project="project"
        />
      </div>
      <div class="p-config-keywords-add__item">
        <p class="p-config-keywords-add__title--sub">
          これからSEO対策を進める場合は、ページのURLを下記から指定して登録してください（URLは完全一致のみ）
        </p>
        <InputUrl :url="project.siteUrl">
          <Input
            name="url"
            v-model="params.url"
            placeholder="/sample/"
            :class="{
              alert: isEdited.url && (error.url || error.id)
            }"
            @focusout="focusoutUrl"
          />
          <Alert v-if="isEdited.url && error.url">
            {{ error.url }}
          </Alert>
          <Alert v-else-if="isEdited.keyword && isEdited.url && error.id">
            {{ error.id }}
          </Alert>
        </InputUrl>
        <Note v-if="isNoData">
          <template slot="title">
            ご指定いただいたキーワードとURLではSearch
            Consoleでの順位データは30日間以上取得されていません。
          </template>
          <div class="c-text--free">
            <dl>
              <dt>＜考えられる原因＞</dt>
              <dd>
                <ul>
                  <li>
                    キーワードの検索ボリュームがあまりにも少ないためにSearch
                    Console でも順位取得を不定期にしか行っていない。
                  </li>
                  <li>
                    順位が低くユーザーに対しても検索結果画面として表示されていないため順位データが存在しない。
                  </li>
                </ul>
              </dd>
            </dl>
            <p>
              これから<span class="c-text--accent">SEO対策を進める場合</span
              >は、ページのURLを下記から指定して登録してください。
            </p>
          </div>
        </Note>
      </div>
    </div>
    <div class="p-config-keywords-add__footer c-alertPos">
      <Alert v-if="errorResponses.length" variant="center">
        <p v-for="(item, index) in errorResponses" :key="index">{{ item }}</p>
      </Alert>
      <Button
        variant="main"
        size="w240"
        fsize="f16"
        :disabled="isLoading || isError || isProcessing"
        :class="{ 'is-loading': isProcessing }"
        @click="submit"
      >
        登録する
      </Button>
    </div>
  </div>
</template>

<script>
import { USAGE_LABEL, USAGE_COUNT_LABEL } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Input from '@/components/Input';
import InputUrl from '@/components/InputUrl';
import KeywordCandidates from '@/components/config/KeywordCandidates';
import Note from '@/components/Note';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    items: {
      type: Array,
      required: true
    },
    keyword: {
      type: String,
      required: false
    },
    project: {
      type: Object,
      required: true
    },
    url: {
      type: String,
      required: false
    },
    isFocus: {
      type: Boolean,
      required: false
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    Alert,
    Button,
    Input,
    InputUrl,
    KeywordCandidates,
    Note
  },
  data() {
    return {
      candidate: {
        items: [],
        isLoading: false,
        isVisible: false
      },
      error: {
        checked: '',
        id: '',
        keyword: '',
        url: ''
      },
      errorResponses: [],
      params: {
        checked: [],
        keyword: this.keyword || '',
        url: this.url || ''
      },
      timeoutId: null,
      isEdited: {
        checked: false,
        keyword: false,
        url: false
      },
      isProcessing: false
    };
  },
  computed: {
    keywords() {
      // 登録する対象のキーワード&URLのオブジェクト配列を用意する
      let keywords = this.params.checked.slice();
      if (!this.error.id && !this.error.keyword && !this.error.url) {
        if (keywords.length) {
          // 入力したキーワード&URLが、選択したものと重複していないかチェックする
          // 半角スペースや末尾スラッシュを取り除いた状態で比較する
          const keyword = this.getKeyword(this.params.keyword);
          const url = this.$options.filters.removeTrailingSlash(
            this.getFullUrl(this.params.url.trim())
          );
          for (let i = 0; i < keywords.length; i++) {
            const item = keywords[i];
            if (
              item.keyword === keyword &&
              this.$options.filters.removeTrailingSlash(item.url) === url
            ) {
              // 重複していた場合は選択したものだけ返す
              return keywords;
            }
          }
        }
        keywords.push({
          keyword: this.params.keyword,
          url: this.getFullUrl(this.params.url)
        });
      }
      return keywords;
    },
    keywordKeys() {
      return this.items.map(item => {
        return this.getKeywordKey(item.keyword, item.url);
      });
    },
    isError() {
      return this.error.id ||
        this.error.keyword ||
        (this.error.checked && this.error.url)
        ? true
        : false;
    },
    isNoData() {
      // 候補の中に手動入力したキーワードとURLの組み合わせが存在しない時に、アラートを表示する
      if (
        !this.error.id &&
        !this.error.keyword &&
        !this.error.url &&
        this.candidate.items.length
      ) {
        // 半角スペースや末尾スラッシュを取り除いた状態で比較する
        const keyword = this.getKeyword(this.params.keyword);
        const url = this.$options.filters.removeTrailingSlash(
          this.getFullUrl(this.params.url.trim())
        );
        if (
          !this.candidate.items.some(item => {
            return (
              item.keyword === keyword &&
              this.$options.filters.removeTrailingSlash(item.url) === url
            );
          })
        ) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    keywordKeys: {
      handler() {
        this.validateKey();
      }
    },
    'params.checked': {
      handler(value) {
        this.error.checked = new Validation().check().test(value);
        this.isEdited.checked = value.length ? true : false;
      },
      immediate: true
    },
    'params.keyword': {
      handler(value) {
        let keyword = this.getKeyword(value);
        this.error.keyword = new Validation()
          .input()
          .keyword()
          .test(keyword);
        this.isEdited.keyword = keyword ? true : false;

        this.resetCandidate();
        this.validateKey();
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponses = [];
      },
      immediate: true
    },
    'params.url': {
      handler(value) {
        let url = value.trim();
        this.error.url = new Validation().input().test(url);
        this.isEdited.url = url ? true : false;

        this.validateKey();
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponses = [];
      },
      immediate: true
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    focusoutKeyword() {
      this.params.keyword = this.getKeyword(this.params.keyword);
    },
    focusoutUrl() {
      // 先頭が/じゃないURLが入力されていたら、スラッシュを付ける
      if (this.params.url && !/^\//.test(this.params.url)) {
        this.params.url = '/' + this.params.url;
      }
    },
    getCandidates() {
      if (this.candidate.isLoading) {
        return;
      }
      this.candidate.isLoading = true;
      this.candidate.isVisible = true;

      const params = {
        keyword: this.params.keyword
      };
      return apiProjects.keywords
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          this.candidate.items = response.data.items.filter(item => {
            // すでに登録済みの組み合わせは除外する
            let key = this.getKeywordKey(item.keyword, item.url);
            return this.keywordKeys.indexOf(key) === -1;
          });
          this.candidate.isLoading = false;
        })
        .catch(error => {
          this.candidate.isLoading = false;
        });
    },
    getFullUrl(url) {
      return (
        this.$options.filters.removeTrailingSlash(this.project.siteUrl) +
        '/' +
        this.$options.filters.removeLeadingSlash(url)
      );
    },
    getKeyword(keyword) {
      return this.$options.filters.normalizeAllSpaces(keyword.trim());
    },
    getKeywordKey(keyword, url) {
      return utils.convertToHalfWidth(
        this.$options.filters
          .removeTrailingSlash(this.getKeyword(keyword) + '_' + url)
          .toLowerCase()
      );
    },
    getUsages() {
      return Promise.all([this.getUsagesKeywords(), this.getUsagesCount()]);
    },
    getUsagesKeywords() {
      const params = {
        items: 'observation-keywords'
      };
      return apiProjects.usages.get(params, this.auth.idToken, this.project.id);
    },
    getUsagesCount() {
      const params = {
        items: 'observation-keywords'
      };
      return apiProjects.usages.count.get(
        params,
        this.auth.idToken,
        this.project.id
      );
    },
    resetCandidate() {
      this.candidate.items = [];
      this.candidate.isVisible = false;
      this.params.checked = [];
    },
    submit() {
      if (this.isError || this.isProcessing) {
        return;
      }
      this.isProcessing = true;
      this.errorResponses = [];

      // 利用量を取得
      this.getUsages()
        .then(response => {
          const count = this.keywords.length; //登録したい数
          const usages = response[0].data.items[0]; //登録数
          const usagesCount = response[1].data; //登録回数
          const usagesRest = usages.max - usages.current;
          const usagesRestCount = usagesCount.max - usagesCount.current;

          if (usagesCount.current + count > usagesCount.max) {
            this.errorResponses.push(
              `${USAGE_COUNT_LABEL['observation-keywords']}の上限に達しています。今月登録できるのは残り${usagesRestCount}件です。`
            );
          } else if (usages.current + count > usages.max) {
            this.errorResponses.push(
              `${USAGE_LABEL['observation-keywords']}の上限に達しています。登録できるのは${usagesRest}件までです。`
            );
          }
          if (this.errorResponses.length) {
            this.isProcessing = false;
            return;
          }

          Promise.all(
            this.keywords.map(item => {
              return this.postKeyword(item);
            })
          )
            .then(response => {
              let keywords = this.keywords.slice();

              for (let i = 0; i < response.length; i++) {
                let id = response[i].data.id;
                // IDの重複を防ぐ
                if (
                  process.env.VUE_APP_ENV === 'local' ||
                  process.env.VUE_APP_ENV === 'demo' ||
                  process.env.VUE_APP_ENV === 'prototype'
                ) {
                  id += Date.now() + i;
                }
                keywords[i].id = id;
              }
              this.$emit('add', keywords);

              this.params.keyword = '';
              this.params.url = '';
              this.isProcessing = false;
            })
            .catch(error => {
              this.isProcessing = false;

              // 削除失敗時のエラーメッセージを消すのに丁度いいトリガーがないので、3秒後に消すようにする
              this.timeoutId = setTimeout(() => {
                this.errorResponses = [];
              }, 3000);
            });
        })
        .catch(error => {
          if (error.response) {
            this.errorResponses = [error.response.data.errors[0].userMessage];
          }
          this.isProcessing = false;
        });
    },
    postKeyword(item) {
      let params = {
        keyword: item.keyword,
        url: item.url
      };
      return apiProjects.observationKeywords
        .post(params, this.auth.idToken, this.project.id)
        .catch(error => {
          if (error.response) {
            this.errorResponses.push(error.response.data.errors[0].userMessage);
          }
        });
    },
    validateKey() {
      if (this.isEdited.keyword && this.isEdited.url) {
        let key = this.getKeywordKey(
          this.params.keyword,
          this.getFullUrl(this.params.url)
        );
        this.error.id = new Validation()
          .notRegistered(this.keywordKeys, 'すでに登録されている組み合わせです')
          .test(key);
      } else {
        this.error.id = '';
      }
    }
  }
};
</script>
